import React, { useEffect, useMemo, useState } from 'react';
import FormError from '../feedback/FormError';
import { toast } from 'react-hot-toast';
import adminServices from '../../services/AdminServices';
import { HiTrash } from 'react-icons/hi'; // Make sure you have this icon
import utils from '../../utils';
import moment from 'moment';
import { FaCheck, FaX } from 'react-icons/fa6';

function UserModal({ action, activeUser, getUsers }) {
  // Form state
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isVendor, setIsVendor] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [passwordType, setPasswordType] = useState('password');
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // Tabs logic
  const tabs = useMemo(() => {
    const baseTabs = ['Info'];
    if (action === 1) {
      return [...baseTabs, 'Delete'];
    }
    return baseTabs;
  }, [action]);

  // Sync form with activeUser when modal opens or user changes
  useEffect(() => {
    setError(null);
    if (action === 0) {
      // Reset for "Add"
      setEmail('');
      setFullName('');
      setPassword('');
      setIsAdmin(false);
      setIsVendor(false);
      setIsVerified(false);
    } else if (action === 1 && activeUser) {
      // Populate for "Update"
      setEmail(activeUser.email || '');
      setFullName(activeUser.full_name || '');
      setIsAdmin(Boolean(activeUser.is_admin));
      setIsVendor(Boolean(activeUser.is_vendor));
      setIsVerified(Boolean(activeUser.is_verified));
    }
  }, [action, activeUser]);

  const togglePassword = () => {
    setPasswordType(passwordType === 'password' ? 'text' : 'password');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const submitToast = toast.loading(
      action === 0 ? 'Adding user account' : 'Updating user account'
    );

    const formData = new FormData();

    if (action === 0) {
      if (password.length < 8) {
        toast.error('Password length should be at least 8 characters');
        setLoading(false);
        return;
      }
      formData.append('password', password);
    }

    formData.append('email', email.trim());
    if (fullName.trim()) {
      formData.append('full_name', fullName.trim());
    }
    formData.append('is_admin', isAdmin);
    formData.append('is_vendor', isVendor);
    formData.append('is_verified', isVerified);

    const apiCall =
      action === 0
        ? adminServices.addUser(formData)
        : adminServices.updateUser(activeUser?.id, formData);

    apiCall
      .then((res) => {
        setLoading(false);
        toast.success(`User ${action === 0 ? 'added' : 'updated'} successfully`, {
          id: submitToast,
        });
        const modalCloseBtn = document.getElementById('user-create-modal-close-btn');
        if (modalCloseBtn) modalCloseBtn.click();
        getUsers();
      })
      .catch((err) => {
        setLoading(false);
        let errorMessage = 'Something went wrong';

        if (err.response?.status === 400 && Array.isArray(err.response.data?.details)) {
          const messages = err.response.data.details.map(d => {
            const fieldName = d.field ? `${d.field.charAt(0).toUpperCase() + d.field.slice(1)}` : 'Field';
            return `${fieldName}: ${d.message}`;
          });
          errorMessage = messages.join('; ') || 'Validation failed';
        } else if (err.response?.status === 400 && err.response.data) {
          const data = err.response.data;
          const fieldErrors = Object.entries(data)
            .flatMap(([field, msgs]) =>
              (Array.isArray(msgs) ? msgs : [msgs])
                .filter(msg => typeof msg === 'string')
                .map(msg => `${field.charAt(0).toUpperCase() + field.slice(1)}: ${msg}`)
            );
          errorMessage = fieldErrors.join('; ') || 'Invalid input';
        } else if (err.request) {
          errorMessage = 'Network error — please check your connection';
        } else {
          errorMessage = err.message || 'Something went wrong';
        }

        setError(errorMessage);
        toast.error('Failed to save user', { id: submitToast });
      });
  };

  const handleDelete = () => {
    if (!activeUser?.id) return;

    if (!window.confirm('Are you sure? This action cannot be undone.')) return;

    setDeleteLoading(true);
    adminServices
      .deleteUser(activeUser.id)
      .then(() => {
        setDeleteLoading(false);
        toast.success('User deleted successfully');
        const modalCloseBtn = document.getElementById('user-create-modal-close-btn');
        if (modalCloseBtn) modalCloseBtn.click();
        getUsers();
      })
      .catch((err) => {
        setDeleteLoading(false);
        toast.error('Failed to delete user');
        console.error(err);
      });
  };

  const changeTab = (idx) => {
    if (action === 1) {
      setActiveTab(idx);
    }
  };

  return (
    <div>
      <label htmlFor="user-create-modal" id="user-create-open-btn" className="hidden">
        Add / Update User
      </label>
      <input type="checkbox" id="user-create-modal" className="modal-toggle" />
      <div className="modal">
        <div className="modal-box w-8/11 max-w-2xl">
          <label
            htmlFor="user-create-modal"
            id="user-create-modal-close-btn"
            className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
          >
            ✕
          </label>
          <h2 className="text-lg font-bold mb-2">
            {action === 0 ? 'Add' : 'Update'} User
          </h2>

          {/* Tabs */}
          <div className="flex justify-center mb-6">
            <div className="border-gray-200 dark:border-border-dark">
              <div className="tabs tabs-boxed justify-center">
                {tabs.map((tab, idx) => (
                  // eslint-disable-next-line jsx-a11y/anchor-is-valid
                  <a
                    key={idx}
                    className={`tab ${activeTab === idx
                      ? 'tab-active bg-primary text-white rounded-lg'
                      : ''
                      }`}
                    onClick={() => changeTab(idx)}
                    role="tab"
                    aria-selected={activeTab === idx}
                  >
                    {tab}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* User Info Tab */}
          {activeTab === 0 && (
            <>
              {action === 1 && activeUser && (
                <div className='mb-4 grid grid-cols-1 gap-3 md:grid-cols-2'>
                  <div className="flex justify-center items-center">
                    {/* Placeholder avatar since no image */}
                    <div className="avatar placeholder">
                      <div className="bg-neutral text-neutral-content text-2xl rounded-full h-36 w-36">
                        {utils.makeProfileLetter(activeUser)}
                      </div>
                    </div>
                  </div>
                  <div className='border p-3 rounded-lg shadow-md'>
                    <p className="font-bold mb-1">Overview</p>
                    <div>
                      <p><span className='font-bold mr-2'>User type:</span> {utils.getUserRoleLabel(activeUser)}</p>
                      {activeUser?.is_vendor ? (
                        <>
                          <p className='flex items-center'>
                            <span className='font-bold mr-2'>Verified</span>
                            {activeUser?.is_verified ? (
                              <FaCheck />
                            ) : (
                              <FaX />
                            )}
                          </p>
                          <p><span className='font-bold mr-2'>Store Name:</span> {activeUser?.store_name || '-'}</p>
                          <p><span className='font-bold mr-2'>Product Count:</span> {activeUser?.product_count}</p>
                        </>
                      ) : (
                        <>
                          <p><span className='font-bold mr-2'>Purchases:</span> {activeUser?.purchase_count || 0}</p>
                          {/* <p><span className='font-bold mr-2'>Purchase Count:</span> {activeUser?.product_count}</p> */}
                        </>
                      )}
                      <p>Created On: <span className='font-bold ml-3 text-gray-600'>{moment(activeUser?.creation_time).format("MMM Do YYYY")}</span></p>
                    </div>
                  </div>
                </div>
              )}
              <form onSubmit={handleSubmit}>
                {error && <FormError error={error} />}

                <div className="form-control mb-3">
                  <label className="label">
                    <span className="label-text">Email:</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input input-bordered w-full"
                  />
                </div>

                <div className="form-control mb-3">
                  <label className="label">
                    <span className="label-text">Full Name:</span>
                  </label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="input input-bordered w-full"
                  />
                </div>

                {action === 0 && (
                  <div className="form-control mb-3">
                    <label className="label">
                      <span className="label-text">Password:</span>
                    </label>
                    <input
                      type={passwordType}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="input input-bordered w-full"
                      required
                    />
                  </div>
                )}

                <div className="flex justify-between">
                  <div className="mb-3">
                    <label className="label">
                      <span className="label-text">Admin Account:</span>
                    </label>
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={isAdmin}
                      onChange={(e) => setIsAdmin(e.target.checked)}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="label">
                      <span className="label-text">Vendor Account:</span>
                    </label>
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={isVendor}
                      onChange={(e) => setIsVendor(e.target.checked)}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="label">
                      <span className="label-text">Verified Account:</span>
                    </label>
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={isVerified}
                      onChange={(e) => setIsVerified(e.target.checked)}
                    />
                  </div>
                </div>

                {action === 0 && (
                  <div className="mb-4">
                    <label className="label cursor-pointer">
                      <span className="label-text">Show Password</span>
                      <input
                        type="checkbox"
                        className="checkbox ml-2"
                        checked={passwordType === 'text'}
                        onChange={togglePassword}
                      />
                    </label>
                  </div>
                )}

                <div className="mt-4">
                  <button
                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-75"
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : action === 0 ? 'Create User' : 'Update User'}
                  </button>
                </div>
              </form>
            </>

          )}

          {/* Delete Tab */}
          {activeTab === 1 && action === 1 && (
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-border-light dark:border-border-dark overflow-hidden">
              <div className="p-6 md:p-8">
                <div className="border-l-4 border-error pl-4 py-2">
                  <h3 className="font-bold text-error">Danger Zone</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    Deleting this user is irreversible. All associated data will be removed.
                  </p>
                  <div className="pt-4">
                    {deleteLoading ? (
                      <div className="flex justify-center py-2">
                        <span className="loading loading-spinner loading-sm"></span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={handleDelete}
                        className="flex items-center space-x-2 bg-error text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-error/90 transition-colors"
                      >
                        <HiTrash className="h-4 w-4" />
                        <span>Delete User</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserModal;